import type { ReactNode } from 'react';
import { forwardRef } from 'react';
import { Drawer, SxProps, Theme } from '@mui/material';
import { Box } from '../../Layout/Box';
import { Typography } from '../../Typography';
import { CloseButton } from '../../Buttons';

export interface SidebarProps {
  /**
   * Whether the sidebar is open
   */
  open: boolean;

  /**
   * Callback fired when the sidebar should close
   */
  onClose: () => void;

  /**
   * Title displayed in the sidebar header
   */
  title: string;

  /**
   * Content to display in the sidebar body
   */
  children: ReactNode;

  /**
   * Optional icon to display before the title in the header
   */
  icon?: ReactNode;

  /**
   * Accessible label for the close button (e.g. translated "Close").
   * @default 'Close'
   */
  closeButtonAriaLabel?: string;

  /**
   * Optional custom styles for the drawer
   */
  drawerSx?: SxProps<Theme>;

  /**
   * Optional custom styles for the content container
   */
  contentSx?: SxProps<Theme>;
}

const defaultSidebarStyles: SxProps<Theme> = {
  '& .MuiDrawer-paper': {
    width: { xs: '70%', sm: '60%' },
    boxSizing: 'border-box',
    bgcolor: 'background.paper',
  },
};

/**
 * Sidebar Component
 *
 * A reusable drawer component that displays a sidebar with a header (title and close button)
 * and customizable content. Used for navigation and filter sidebars.
 * Renders as a right-anchored MUI Drawer with responsive width.
 *
 * @example
 * ```tsx
 * <Sidebar
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   title="Filters"
 *   closeButtonAriaLabel={translate('map.close')}
 * >
 *   <FilterForm onApply={handleApply} />
 * </Sidebar>
 * ```
 *
 * @example
 * With optional icon in header:
 * ```tsx
 * <Sidebar open={open} onClose={onClose} title="Filters" icon={<FilterIcon />}>
 *   {children}
 * </Sidebar>
 * ```
 */
export const Sidebar = forwardRef<HTMLDivElement, SidebarProps>(function Sidebar(
  { open, onClose, title, children, icon, closeButtonAriaLabel = 'Close', drawerSx, contentSx },
  ref
) {
  const drawerSxArray = Array.isArray(drawerSx) ? drawerSx : drawerSx ? [drawerSx] : [];
  const contentSxArray = Array.isArray(contentSx) ? contentSx : contentSx ? [contentSx] : [];

  return (
    <Drawer
      ref={ref}
      anchor="right"
      open={open}
      onClose={onClose}
      sx={[defaultSidebarStyles, ...drawerSxArray] as SxProps<Theme>}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: '8px 8px 0px 16px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography variant="h2" component="h2">
            {title}
          </Typography>
        </Box>
        <CloseButton onClick={onClose} aria-label={closeButtonAriaLabel} />
      </Box>
      <Box sx={[{ p: 2 }, ...contentSxArray] as SxProps<Theme>}>{children}</Box>
    </Drawer>
  );
});

Sidebar.displayName = 'Sidebar';
